import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import api, { getErrorMessage } from "./api";
import { solveChallenge } from "./proofOfWork";

import { useUserUpdateSocket, useSubscriptionSocket, reconnectSocket } from "@/hooks/useSocket";

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl?: string | null;
  isAdmin: boolean;
  subscriptionTier: "FREE" | "SUBSCRIBER";
  subscriptionStatus?: string;
  subscriptionExpiresAt?: string;
  trialEndsAt?: string | null;
  newsletterConsent?: boolean;
  emailVerified: boolean;
  authProvider?: string;
  timezone?: string;
  currency?: string;
  locale?: string;
  preferredMarketplace?: string;
  phone?: string;
  createdAt?: string;
  _count?: {
    contacts: number;
    giftSearches: number;
    cards: number;
    savedGifts: number;
    reminders: number;
  };
}

export interface PasswordlessResult {
  codeSent?: boolean;
  existingUser?: boolean;
  email?: string;
  devAutoLogin?: boolean;
  user?: User;
  accessToken?: string;
  refreshToken?: string;
  needsProfile?: boolean;
}

export interface VerifyCodeResult {
  user: User;
  needsProfile?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  registerPasswordless: (email: string) => Promise<PasswordlessResult>;
  verifyRegistrationCode: (email: string, code: string) => Promise<VerifyCodeResult>;
  completeProfile: (data: { name: string; newsletterConsent?: boolean; agreedToTerms: boolean }) => Promise<void>;
  loginWithGoogle: (token: string, rememberMe?: boolean) => Promise<{ isNewUser?: boolean; user?: User }>;
  logout: () => void;
  verifyEmail: (token: string) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  deleteAccount: (confirmation: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const setUser = useCallback((user: User | null) => {
    setState({
      user,
      isAuthenticated: !!user,
      isLoading: false,
    });
    // Reconnect WebSocket with current auth token whenever user state changes
    reconnectSocket();
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setState({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    try {
      const response = await api.get("/users/me");
      setUser(response.data.data);
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setUser(null);
    }
  }, [setUser]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Listen for real-time profile updates from the server via WebSocket
  useUserUpdateSocket(
    useCallback(
      (data) => {
        setState((prev) => {
          if (!prev.user || prev.user.id !== data.id) return prev;
          return {
            ...prev,
            user: { ...prev.user, ...data },
          };
        });
      },
      []
    )
  );

  // Listen for real-time subscription changes (Stripe webhooks → WebSocket)
  useSubscriptionSocket(
    useCallback(
      (data) => {
        setState((prev) => {
          if (!prev.user) return prev;
          return {
            ...prev,
            user: {
              ...prev.user,
              subscriptionTier: data.subscriptionTier,
              subscriptionStatus: data.subscriptionStatus,
              ...(data.subscriptionExpiresAt && { subscriptionExpiresAt: data.subscriptionExpiresAt }),
              ...(data.trialEndsAt !== undefined && { trialEndsAt: data.trialEndsAt }),
            },
          };
        });
      },
      []
    )
  );

  const registerPasswordless = useCallback(
    async (email: string): Promise<PasswordlessResult> => {
      try {
        const response = await api.post("/auth/register-passwordless", { email });
        const data = response.data.data;

        // Dev auto-login: store tokens and set user immediately
        if (data.devAutoLogin && data.accessToken && data.refreshToken) {
          localStorage.setItem("access_token", data.accessToken);
          localStorage.setItem("refresh_token", data.refreshToken);
          if (data.user) {
            setUser(data.user as User);
          }
        }

        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    [setUser]
  );

  const verifyRegistrationCode = useCallback(
    async (email: string, code: string): Promise<VerifyCodeResult> => {
      try {
        const response = await api.post("/auth/verify-registration-code", { email, code });
        const { accessToken, refreshToken, user, needsProfile } = response.data.data;

        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", refreshToken);
        setUser(user);

        return { user, needsProfile };
      } catch (error: any) {
        const data = error?.response?.data;
        // If PoW is required, solve it and retry with the same code
        if (data?.requiresPow && data?.powChallenge && data?.powDifficulty) {
          const nonce = await solveChallenge(data.powChallenge, data.powDifficulty);
          try {
            const retryResponse = await api.post("/auth/verify-registration-code", {
              email, code,
              powSolution: { challenge: data.powChallenge, nonce },
            });
            const retryData = retryResponse.data.data;
            localStorage.setItem("access_token", retryData.accessToken);
            localStorage.setItem("refresh_token", retryData.refreshToken);
            setUser(retryData.user);
            return { user: retryData.user, needsProfile: retryData.needsProfile };
          } catch (retryError: any) {
            throw new Error(getErrorMessage(retryError));
          }
        }
        throw new Error(getErrorMessage(error));
      }
    },
    [setUser]
  );

  const completeProfile = useCallback(
    async (data: { name: string; newsletterConsent?: boolean; agreedToTerms: boolean }) => {
      try {
        const response = await api.post("/auth/complete-profile", data);
        setUser(response.data.data.user);
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    [setUser]
  );

  const loginWithGoogle = useCallback(
    async (token: string, rememberMe?: boolean) => {
      try {
        const isTrustedDevice = rememberMe ?? localStorage.getItem("prezntz_trusted_device") === "true";
        const response = await api.post("/auth/google", { idToken: token, rememberMe: isTrustedDevice });
        const { accessToken, refreshToken, user, isNewUser } = response.data.data;

        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", refreshToken);
        localStorage.setItem("auth_provider", "GOOGLE");
        setUser(user);

        return { isNewUser, user };
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    [setUser]
  );

  const logout = useCallback(() => {
    // Revoke Google credential cache to prevent auto-sign-in after explicit logout
    const currentUser = state.user;
    if (currentUser?.authProvider === "GOOGLE" && currentUser?.email) {
      try {
        window.google?.accounts?.id?.revoke(currentUser.email, () => {
          // Revocation complete — credential cache cleared
        });
      } catch {
        // Non-critical — continue logout even if revoke fails
      }
    }

    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("prezntz_trusted_device");
    localStorage.removeItem("auth_provider");
    setUser(null);
  }, [setUser, state.user]);

  const verifyEmail = useCallback(async (token: string) => {
    try {
      await api.post("/auth/verify-email", { token });
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }, []);

  const requestMagicLink = useCallback(async (email: string) => {
    try {
      await api.post("/auth/magic-link", { email });
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }, []);

  const updateProfile = useCallback(
    async (data: Partial<User>) => {
      try {
        const response = await api.put("/users/me", data);
        setUser(response.data.data);
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    [setUser]
  );

  const deleteAccount = useCallback(
    async (confirmation: string) => {
      try {
        await api.delete("/users/me", { data: { confirmation } });
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        setUser(null);
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    [setUser]
  );

  const value: AuthContextType = {
    ...state,
    registerPasswordless,
    verifyRegistrationCode,
    completeProfile,
    loginWithGoogle,
    logout,
    verifyEmail,
    requestMagicLink,
    updateProfile,
    deleteAccount,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
